import React from 'react'
import ReactQuill, { DeltaStatic, Quill } from 'react-quill-new'
import Delta from 'quill-delta'
import { HoverPopover } from '../../layouts/components/popover'
import { IconInfoCircle } from '../../icons/library'
import { getQuillModules, initializeWithFormats, insertDeltasAtCursor, QuillFormat } from './quillTheme'
import { User } from '../../providers/context'
import { useTranslation } from '../../providers/translation'
import { useBufferedUpdates } from '../../hooks/hooks'
import { convertChildrensAttrToTooltip, Tooltip } from '../simple/tooltip'

const editorClasses = `[&_.ql-editor]:!min-h-48 [&_.ql-editor]:!border-none [&_.ql-editor]:!p-2 [&_.ql-editor]:text-[1rem]`
const tooltipLinkEditorClasses = `[&_input]:!ring-0 [&_.ql-tooltip-editor]:absolute [&_.ql-tooltip-editor]:inset-0 [&_.ql-tooltip-editor_input]:!p-1`
const tooltipArrowClasses = `[&_.ql-tooltip:not(.ql-flip)_.ql-tooltip-arrow]:!border-b-6 [&_.ql-tooltip:not(.ql-flip)_.ql-tooltip-arrow]:!border-b-neutral-900`
const tooltipClasses = `[&_.ql-tooltip]:z-50 [&_.ql-tooltip]:!rounded [&_.ql-tooltip]:outline [&_.ql-tooltip]:!outline-1 [&_.ql-tooltip]:!outline-neutral-900 [&_.ql-tooltip]:!bg-neutral-900 [&_.ql-tooltip]:!p-1 [&_.ql-tooltip]:!px-[7px] [&_.ql-tooltip]:!py-[3px] [&_.ql-tooltip]:!text-sm [&_.ql-tooltip]:!text-white [&_.ql-formats]:!m-0 ${tooltipArrowClasses} ${tooltipLinkEditorClasses}`
const linkClasses = `[&_a]:before:hidden [&_a]:after:hidden [&_a]:!text-link`
const listClasses = `[&_ol]:!p-0 [&_li]:!pl-4`

export const getNormalTagsForUser: (user: User) => string[] = (user) => {
  const tags = ['student.homeroom', 'today.date', 'otherData.bus', 'student.class."*"']
  const people = ['student', 'guardian']
  tags.push(...people.flatMap((p) => [`${p}.firstname`, `${p}.lastname`]))
  if (user.type === 'teacher' && user.showAttendance) tags.push('attendance.class')
  return tags
}

interface QuillTextAreaProps {
  /** The HTML value of the textarea. The textarea is controlled by this value */
  valueHTML?: string
  /** Though the textarea is controlled by HTML, you can set the value with a delta. */
  valueDelta?: Delta
  /** Callback function to be called when the value changes. */
  onValueChange: (html: string | undefined, delta: Delta) => void
  /** Class name for the textarea */
  className?: string
  /** Optionally, provide the user to show them info about what they can do with the textarea */
  infoForUser?: User
  /** A caption to show below the textarea. This caption is not shown if the message is long */
  caption?: React.ReactNode
  /** Delay in milliseconds before the value is updated. This is useful to prevent too many updates when typing. Default is 100ms */
  valueUpdateDelay?: number
  /** Allowable formats for the quill editor. Default is ['link'] */
  formats?: QuillFormat[]
  /** Allowed message script tags for the quill editor. For example, ['student.firstname'] */
  tags?: string[]
  /** Whether the textarea is invalid. This will show a red border around the textarea */
  invalid?: boolean
  /** Handler for when an image is pasted into the textarea. */
  imageHandler?: (file: File) => void
  /** Whether to highlight words on the poor translation list */
  flagPoorTranslations?: boolean
  /** Extra content to show below the textarea */
  below?: React.ReactNode
  /** Whether to autofocus the textarea on mount */
  autofocus?: boolean
  /** Whether to show a warning if the message is long (more than 300 characters or 5 lines) */
  warnIfLong?: boolean
  /** Whether to constrain the tooltip to the editor. This is useful for when the editor is inside a container with overflow hidden */
  constrainTooltipWithinEditor?: boolean
  /** Optional placeholder for the textarea */
  placeholder?: string
}

/**
 * Rich text editor using Quill. This editor includes support for message script tags, image pasting, and custom formats.
 * It is a controlled component, meaning that the value is controlled by the `valueHTML` or `valueDelta` props.
 */
export const QuillTextarea: React.FC<QuillTextAreaProps> = ({
  valueHTML,
  valueDelta,
  onValueChange,
  className,
  infoForUser,
  caption,
  valueUpdateDelay = 100,
  formats: rawFormats = ['link'],
  tags = [],
  invalid,
  imageHandler,
  flagPoorTranslations = false,
  below,
  autofocus,
  warnIfLong,
  constrainTooltipWithinEditor = false,
  placeholder,
}) => {
  const [longMessage, setLongMessage] = React.useState<boolean>(false)
  const [internalHTML, setInternalHTML] = useBufferedUpdates(
    valueHTML,
    (v) => {
      // IMPORTANT! it is very important that this function is called every time internalHTML changes because
      // it is used to update the delta value. If we dont call this function, the delta value will silently be
      // out of sync with the html value. Be careful if you change stuff with this or useBufferedUpdates
      onValueChange(v, quillRef.current!.getContents())
      if (editorNodeRef.current) convertChildrensAttrToTooltip(editorNodeRef.current)
      updateLongMessage()
    },
    valueUpdateDelay,
    true
  )

  // allows you to set the value with a delta, even though the textArea is controlled with html
  React.useEffect(() => {
    if (valueDelta && valueHTML === undefined && quillRef.current) {
      quillRef.current.setContents(valueDelta)
    }
  }, [valueDelta, valueHTML])

  const updateLongMessage = () => {
    if (quillRef.current) setLongMessage(quillRef.current.getLength() > 300 || quillRef.current.getLines().length > 5)
  }

  const formats = React.useMemo(() => {
    let formats = [...rawFormats]
    if (tags.length > 0) formats.push('message-script')
    if (flagPoorTranslations) formats.push('flagged')
    return formats
  }, [rawFormats])

  const nextFocusRef = React.useRef<HTMLDivElement | null>(null)
  const quillRef = React.useRef<Quill | null>(null)
  const editorNodeRef = React.useRef<HTMLDivElement | null>(null)
  const initQuill = (node: ReactQuill | null) => {
    if (!node || node?.getEditor() === quillRef.current) return
    if (autofocus) node.focus()
    quillRef.current = node.getEditor()
    initializeWithFormats(quillRef.current, formats, tags, flagPoorTranslations, imageHandler)

    // blurQuill is a custom property used to fix a weird bug. see templatesrow for more
    const editorNode = node.editingAreaRef.current!.querySelector('.ql-editor')
    editorNodeRef.current = editorNode
    if (editorNode)
      editorNode.blurQuill = () => {
        node.blur()
      }

    editorNode.addEventListener('keydown', (e: KeyboardEvent) => {
      if (e.key === 'Escape' && nextFocusRef.current) nextFocusRef.current.focus()
    })

    updateLongMessage()
  }

  const modules = React.useMemo(() => getQuillModules(formats), [formats])

  const randomID = React.useMemo(() => Math.random().toString(36).substring(7), [])

  const border = `transition-colors duration-150 border ${invalid ? 'border-red-500 z-50' : 'border-gray-200'}`
  return (
    <div className={`relative flex min-h-48 flex-col sm:flex-grow ${border} ${className || ''}`}>
      <ReactQuill
        id={`quill-${randomID}`}
        theme="bubble"
        modules={modules}
        value={internalHTML}
        onChange={setInternalHTML}
        className={`h-full w-full ${editorClasses} ${tooltipClasses} ${linkClasses} ${listClasses}`}
        ref={initQuill}
        formats={formats}
        bounds={constrainTooltipWithinEditor ? `#quill-${randomID}` : undefined}
        placeholder={placeholder}
      />
      {infoForUser && (
        <HoverPopover trigger={<IconInfoCircle className="absolute right-2 top-2 opacity-10" />}>
          <QuillTextareaInfo
            user={infoForUser}
            insert={(deltas) => {
              const quill = quillRef.current
              if (!quill) return
              insertDeltasAtCursor(quill, deltas)
            }}
          />
        </HoverPopover>
      )}
      {caption && !longMessage && (
        <div className="pointer-events-none absolute bottom-2 left-2 z-10 text-sm text-slate-300">{caption}</div>
      )}
      {longMessage && warnIfLong && (
        <Tooltip text="Text messages may not show this entire message, but they will end with a link to see the rest of the message.">
          <div className="absolute bottom-2 right-4 z-10 select-none text-sm text-logo">long message</div>
        </Tooltip>
      )}
      {below}
      <div className="pointer-events-none absolute inset-0" tabIndex={-1} ref={nextFocusRef} />
    </div>
  )
}
// test www.google.com test
interface QuillTextareaInfoProps {
  user: User
  insert: (deltas: DeltaStatic['ops']) => void
}

export const QuillTextareaInfo: React.FC<QuillTextareaInfoProps> = ({ user, insert }) => {
  const { tr } = useTranslation()
  return (
    <div className="flex max-w-sm flex-col p-3">
      <b>{tr('Text settings')}</b>
      <span>
        {tr('Select text to see formatting options. Alternatively, control or command + b for ')}
        <b>{tr('bold')}</b>
        {tr(' control or command + i for ')}
        <i>{tr('italic')}</i>
        {user.type === 'teacher' && (
          <>
            <br />
            <br />
            Auto replace tags:
            <div className="my-2 flex flex-col items-start gap-1 leading-4">
              <div
                onClick={() => insert([{ insert: { 'message-script': '[student.firstname]' } }, { insert: ' ' }])}
                className="message-script cursor-pointer"
              >
                [student.firstname]
              </div>
              <div
                onClick={() => insert([{ insert: { 'message-script': '[student.lastname]' } }, { insert: ' ' }])}
                className="message-script cursor-pointer"
              >
                [student.lastname]
              </div>
              <div
                onClick={() => insert([{ insert: { 'message-script': '[guardian.firstname]' } }, { insert: ' ' }])}
                className="message-script cursor-pointer"
              >
                [guardian.firstname]
              </div>
              <div
                onClick={() => insert([{ insert: { 'message-script': '[guardian.lastname]' } }, { insert: ' ' }])}
                className="message-script cursor-pointer"
              >
                [guardian.lastname]
              </div>
            </div>
            <p>
              {tr(
                'These tags are auto replaced with the corresponding info of each recipient. Type the entire contents, e.g., '
              )}
              [student.firstname]
              {tr(' to create a tag.')}
            </p>
          </>
        )}
        <br />
        {tr('To prevent a word or phrase from being translated, surround it in parenthesis and double quotes:')}&nbsp;("
        <i>word here</i>")
      </span>
    </div>
  )
}

export const textToDelta = (text: string) => {
  return new Delta(text.split('\n').map((line) => ({ insert: `${line}\n` })))
}

export const isDeltaEmpty = (delta: Delta) => {
  return !delta.ops.some((o) => typeof o.insert !== 'string' || o.insert !== '\n')
}

export const deltaToText = (delta?: Delta) => {
  return (delta?.ops || []).map((op) => (typeof op.insert === 'string' ? op.insert : ' ')).join(' ')
}
